import { isOverdue, newId } from '@/lib/format'
import type { Expense, Payment, WeddingData } from '@/mock/types'

/** The table below the summary: items by category, or every payment by due date. */
export type BudgetView = 'items' | 'timeline'

export const parseView = (value: string | undefined): BudgetView =>
  value === 'timeline' ? 'timeline' : 'items'

export const budgetFilters = ['all', 'due', 'overdue', 'estimate', 'unpriced', 'paid'] as const
export type BudgetFilter = (typeof budgetFilters)[number]

export const filterLabels: Record<BudgetFilter, string> = {
  all: 'Vše',
  due: 'K zaplacení',
  overdue: 'Po splatnosti',
  estimate: 'Odhady',
  unpriced: 'Bez ceny',
  paid: 'Zaplacené',
}

export function parseFilter(value: string | undefined): BudgetFilter {
  return budgetFilters.find((filter) => filter === value) ?? 'all'
}

export const paidAmount = (expense: Expense) =>
  expense.payments.reduce((sum, payment) => sum + (payment.paid ? payment.amount : 0), 0)

const overduePayments = (expense: Expense) =>
  expense.payments.filter((payment) => !payment.paid && isOverdue(payment.dueDate))

/** The next unpaid payment: the earliest due date first (overdue ones lead), then undated ones. */
export function nextPayment(expense: Expense): Payment | null {
  const unpaid = expense.payments.filter((payment) => !payment.paid)
  return (
    unpaid
      .filter((payment) => payment.dueDate)
      .sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''))[0] ??
    unpaid[0] ??
    null
  )
}

export type ExpenseStatus = 'unpriced' | 'estimate' | 'overdue' | 'paid' | 'partly' | 'unpaid'

export function expenseStatus(expense: Expense): ExpenseStatus {
  if (expense.amount === 0) return 'unpriced'
  if (overduePayments(expense).length) return 'overdue'
  const paid = paidAmount(expense)
  if (paid >= expense.amount) return 'paid'
  if (paid > 0) return 'partly'
  return expense.estimate ? 'estimate' : 'unpaid'
}

export const statusLabels: Record<ExpenseStatus, string> = {
  unpriced: 'Bez ceny',
  estimate: 'Odhad',
  overdue: 'Po splatnosti',
  paid: 'Zaplaceno',
  partly: 'Zčásti zaplaceno',
  unpaid: 'Nezaplaceno',
}

export const statusTones = {
  unpriced: 'warning',
  estimate: 'neutral',
  overdue: 'danger',
  paid: 'success',
  partly: 'neutral',
  unpaid: 'neutral',
} as const

export function matchesFilter(expense: Expense, filter: BudgetFilter) {
  const status = expenseStatus(expense)
  switch (filter) {
    case 'all':
      return true
    case 'due':
      return (
        status === 'overdue' || status === 'partly' || status === 'unpaid' || status === 'estimate'
      )
    case 'overdue':
      return status === 'overdue'
    case 'estimate':
      return expense.estimate && expense.amount > 0
    case 'unpriced':
      return status === 'unpriced'
    case 'paid':
      return status === 'paid'
  }
}

/** The item's category: a supplier item is filed under the supplier's category. */
export function expenseCategory(expense: Expense, data: WeddingData) {
  return data.suppliers.find((item) => item.id === expense.supplierId)?.category ?? expense.category
}

/** The item's display name: a supplier item is named after the supplier. */
export function expenseTitle(expense: Expense, data: WeddingData) {
  return data.suppliers.find((item) => item.id === expense.supplierId)?.name ?? expense.title
}

export function matchesSearch(expense: Expense, data: WeddingData, query: string) {
  const needle = query.trim().toLocaleLowerCase('cs')
  return !needle || expenseTitle(expense, data).toLocaleLowerCase('cs').includes(needle)
}

export function budgetTotals(data: WeddingData) {
  const { expenses, total } = data.budget
  const sum = (items: Expense[], value: (expense: Expense) => number) =>
    items.reduce((result, expense) => result + value(expense), 0)
  const planned = sum(expenses, (expense) => expense.amount)
  const estimated = sum(
    expenses.filter((expense) => expense.estimate),
    (expense) => expense.amount,
  )
  const paid = sum(expenses, paidAmount)
  const overdue = sum(expenses, (expense) =>
    overduePayments(expense).reduce((result, payment) => result + payment.amount, 0),
  )
  return {
    total,
    planned,
    agreed: planned - estimated,
    estimated,
    estimates: expenses.filter((expense) => expense.estimate && expense.amount > 0).length,
    unallocated: total === null ? null : total - planned,
    paid,
    remaining: planned - paid,
    overdue,
  }
}

/** Marks one payment paid and finishes the payment task that points at it. */
export function markPaid(data: WeddingData, expenseId: string, paymentId: string): WeddingData {
  return {
    ...data,
    budget: {
      ...data.budget,
      expenses: data.budget.expenses.map((expense) =>
        expense.id === expenseId
          ? {
              ...expense,
              payments: expense.payments.map((payment) =>
                payment.id === paymentId ? { ...payment, paid: true } : payment,
              ),
            }
          : expense,
      ),
    },
    tasks: data.tasks.map((task) =>
      task.payment?.expenseId === expenseId && task.payment.paymentId === paymentId
        ? { ...task, done: true }
        : task,
    ),
  }
}

/**
 * Keeps one task in Úkoly per payment with a due date: adds
 * tasks for new ones, follows changed dates and the paid state, and drops
 * tasks whose payment is gone or has no due date any more.
 */
export function syncPaymentTasks(data: WeddingData, expense: Expense, title: string): WeddingData {
  const byPayment = new Map(
    data.tasks
      .filter((task) => task.payment?.expenseId === expense.id)
      .map((task) => [task.payment?.paymentId, task]),
  )
  const kept = data.tasks.filter((task) => {
    if (task.payment?.expenseId !== expense.id) return true
    const payment = expense.payments.find((item) => item.id === task.payment?.paymentId)
    return Boolean(payment?.dueDate)
  })
  const tasks = kept.map((task) => {
    if (task.payment?.expenseId !== expense.id) return task
    const payment = expense.payments.find((item) => item.id === task.payment?.paymentId)
    return payment ? { ...task, dueDate: payment.dueDate, done: payment.paid } : task
  })
  const added = expense.payments
    .filter((payment) => payment.dueDate && !byPayment.has(payment.id))
    .map((payment) => ({
      id: newId('task'),
      title: `Zaplatit: ${title}, ${payment.name.toLocaleLowerCase('cs') || 'platba'}`,
      note: '',
      dueDate: payment.dueDate,
      priority: 'normal' as const,
      assigneeId: null,
      labels: ['Platby'],
      done: payment.paid,
      payment: { expenseId: expense.id, paymentId: payment.id },
    }))
  return { ...data, tasks: [...added, ...tasks] }
}
